import React from 'react';
import cn from 'clsx';
import styles from './style.module.scss';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { TextArea } from '@components-clean/core/text-area';
import { ObservationDetailsDto, ObservationType } from '@interfaces-clean/observations.interface';
import { SubjectObservationType } from '@interfaces-clean/subject-monitoring/subject-observations';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';

interface ObservationDetailsProps {
    observation?: ObservationDetailsDto;
    observationLoading: boolean;
    type: ObservationType | SubjectObservationType;
    area?: 'CM' | 'SM';
}

export const ObservationDetails = ({ observation, observationLoading, type, area }: ObservationDetailsProps) => {
    const { t } = useTranslation('cm');

    if (observationLoading) {
        return <Loading />;
    }

    if (!observation) {
        return (
            <div className="k-h-full k-p-8">
                <InfoField type="Error" message={t('observationDetails.error')} />
            </div>
        );
    }

    const showDataListing = !observation.panel && !!observation.dataListing;
    const panelOrDataListingLabel = showDataListing
        ? t('sm:monitoringToolkit.editObservationDialog.dataListing')
        : t('observationDialog.panel');
    const panelOrDataListingValue = showDataListing ? observation.dataListing : (observation.panel ?? 'N/A');
    const panelOrDataListingInactive = showDataListing
        ? observation.dataListingActive === 'No'
        : observation.panelActive === 'No';

    return (
        <div className={cn(styles.observationDetails)}>
            <div className="k-d-flex k-gap-12px k-align-items-stretch">
                <div className={cn(styles.label, 'k-font-medium')}>{t('observationDialog.id')}</div>
                <div className={cn(styles.content, '')}>{observation.recordId}</div>
            </div>
            {(type === ObservationType.Site || type === ObservationType.Controls) && (
                <div className="k-d-flex">
                    <div className={cn(styles.label, 'k-font-medium')}>{t('observationDialog.type')}</div>
                    <div className={cn(styles.content, '')}>{observation.type}</div>
                </div>
            )}
            <div className="k-d-flex k-gap-12px k-align-items-stretch">
                <div className={cn(styles.label, 'k-font-medium')}>{t('observationDialog.status')}</div>
                <div className={cn(styles.content, '')}>{observation?.status}</div>
            </div>

            {area === 'SM' && (
                <div className="k-d-flex k-gap-12px k-align-items-stretch">
                    <div className={cn(styles.label, 'k-font-medium')}>
                        {t('sm:monitoringToolkit.editObservationDialog.subject')}
                    </div>
                    <div className={cn(styles.content, '')}>{observation?.subject}</div>
                </div>
            )}

            <div className="k-d-flex k-gap-12px k-align-items-stretch">
                <div className={cn(styles.label, 'k-font-medium')}>{t('observationDialog.dataset')}</div>
                <div className={cn(styles.content, '')}>
                    {stringDateWithoutTimeFromUtc(observation?.datasetOpenedOn ?? null, 'DD-MMM-YYYY') as string}
                </div>
            </div>
            {type === ObservationType.Site && (
                <div className="k-d-flex">
                    <div className={cn(styles.label, 'k-font-medium')}>{t('observationDialog.siteLabel')}</div>
                    <div className={cn(styles.content, '')}>{observation.siteName}</div>
                </div>
            )}
            {type !== ObservationType.Controls && (
                <div className="k-d-flex k-gap-12px k-align-items-stretch k-align-items-center">
                    <div className={cn(styles.label, 'k-font-medium k-align-items-center')}>
                        {t('observationDialog.associationLabel')}
                    </div>
                    <div className={cn(styles.content, ' k-d-flex k-align-items-center')}>
                        {observation?.association ?? 'N/A'}
                        {observation?.associationActive === 'No' && (
                            <div className={cn(styles.inactiveIcon, 'k-pl-sm')}>
                                <InactiveIcon
                                    toolTipText={t('common:inactiveIcon')}
                                    dataTestId="inactive-icon"
                                    tooltipPosition="bottom"
                                    className="k-mr-2"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div className="k-d-flex k-align-items-stretch">
                <div className={cn(styles.label, 'k-font-medium k-align-items-center')}>{panelOrDataListingLabel}</div>
                <div
                    className={cn(styles.content, styles.wrap, '!k-basis-2/3 k-flex-row k-d-flex k-align-items-center')}
                >
                    {panelOrDataListingValue}
                    {panelOrDataListingInactive && (
                        <div className={cn(styles.inactiveIcon, 'k-pl-sm')}>
                            <InactiveIcon
                                toolTipText={t('common:inactiveIcon')}
                                dataTestId="inactive-icon"
                                tooltipPosition="bottom"
                                className="k-mr-2"
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="k-d-flex k-gap-12px k-align-items-stretch">
                <div className={cn(styles.label, 'k-font-medium k-align-items-center')}>
                    {t('observationDialog.owner')}
                </div>
                <div
                    className={cn(
                        styles.content,
                        styles.wrap,
                        '!k-basis-2/3  k-flex-row k-d-flex k-align-items-center',
                    )}
                >
                    {observation?.ownerName ?? 'N/A'}
                    {observation?.ownerNameActive === 'No' && (
                        <div className={cn(styles.inactiveIcon, 'k-pl-sm')}>
                            <InactiveIcon
                                toolTipText={t('common:inactiveIcon')}
                                dataTestId="inactive-icon"
                                tooltipPosition="bottom"
                                className="k-mr-2"
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className="k-d-flex k-gap-12px k-align-items-stretch">
                <div className={cn(styles.label, 'k-font-medium k-align-items-center')}>
                    {t('observationDialog.decision')}
                </div>
                <div className={cn(styles.content, '!k-basis-2/3 k-d-flex k-align-items-center')}>
                    {observation?.decision ?? 'N/A'}
                    {observation?.decisionActive === 'No' && (
                        <div className={cn(styles.inactiveIcon, 'k-pl-sm')}>
                            <InactiveIcon
                                toolTipText={t('common:inactiveIcon')}
                                dataTestId="inactive-icon"
                                tooltipPosition="bottom"
                                className="k-mr-2"
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className={cn(styles.observationComment, 'k-flex-auto k-gap-12px k-d-flex k-flex-col')}>
                <div className={cn(styles.label, 'k-font-medium')}>{t('observationDialog.observationLabel')}</div>
                <div className={cn(styles.content, 'k-flex-auto k-d-flex k-min-h-0 ')}>
                    <TextArea
                        data-testid="observation-details-observation"
                        readOnly
                        value={observation?.observation ?? undefined}
                        className="k-h-full"
                    />
                </div>
            </div>
            <div className={cn('k-gap-12px k-d-flex k-flex-col')}>
                <div className={cn(styles.label, 'k-font-medium')}>{t('observationDialog.comments')}</div>
                <div className={cn(styles.content, 'k-min-h-0 k-flex-auto k-d-flex')}>
                    <TextArea
                        data-testid="observation-details-comments"
                        readOnly
                        value={observation?.comments ?? undefined}
                        style={{
                            height: observation.comments ? '88px' : '27px',
                            overflow: observation.comments ? 'auto' : 'none',
                        }}
                    />
                </div>
            </div>
        </div>
    );
};
